const API_BASE = '/api';

export class ApiClientError extends Error {
  constructor(public status: number, public code: string, message: string, public issues?: unknown) {
    super(message);
    this.name = 'ApiClientError';
  }
}

function readCookie(name: string) {
  if (typeof document === 'undefined') return '';
  const item = document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`));
  return item ? decodeURIComponent(item.slice(name.length + 1)) : '';
}

function isMutation(method = 'GET') {
  return !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
}

let refreshPromise: Promise<boolean> | null = null;
async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

async function request<T>(path: string, options: RequestInit = {}, retry = true): Promise<T> {
  const method = options.method ?? 'GET';
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (isMutation(method)) {
    const csrf = readCookie('csrf_token');
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }
  const response = await fetch(`${API_BASE}${path}`, { ...options, method, headers, credentials: 'include' });
  if (response.status === 401 && retry && path !== '/auth/refresh' && await refreshAccessToken()) {
    return request<T>(path, options, false);
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: { code: 'UNKNOWN', message: response.statusText } }));
    const error = body?.error ?? { code: 'UNKNOWN', message: 'Request failed' };
    throw new ApiClientError(response.status, error.code, error.message, error.issues);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T, B = unknown>(path: string, body?: B) => request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T, B = unknown>(path: string, body?: B) => request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T, B = unknown>(path: string, body?: B) => request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T, B = unknown>(path: string, body?: B) => request<T>(path, { method: 'DELETE', body: body === undefined ? undefined : JSON.stringify(body) }),
  upload: <T>(path: string, formData: FormData) => request<T>(path, { method: 'POST', body: formData }),
};

export default api;
